import React, { useState, useEffect } from 'react';
import Menu from '../menu/Menu'; // Import existing Menu component
import SelectPG from './SelectPG'; // Import SelectPG component
import SelectBed from './SelectBed'; // Import SelectBed component
import UpdateAllocationStatusModal from './UpdateAllocationStatusModal'; // Import UpdateAllocationStatusModal
import DatePicker from 'react-datepicker'; // Import React DatePicker
import 'react-datepicker/dist/react-datepicker.css'; // DatePicker styles
import RTButton from '../common/RTButton'; // Import RTButton
import RTSpinner from '../common/RTSpinner'; // Import RTSpinner
import '../common/rtCommon.css'; // Common styles
import './AllocateBed.css'; // Specific styles for AllocateBed
import { useNavigate, useLocation } from 'react-router-dom';
import { FaEdit, FaTimes } from 'react-icons/fa'; // Add this import for icons
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import { apiPost } from '../../api/apiClient';
import { toastError, toastSuccess, toastConfirm } from '../common/toast'; // Import custom toast

const formatDateToYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const AllocateBed = () => {
  const navigate = useNavigate();
  const location = useLocation();
 
  // Get the pre-selected bed from navigation state
  const preSelectedBed = location.state?.selectedBed || null;
  const preSelectedPG = location.state?.selectedPG || null;
  const [startDate, setStartDate] = useState(new Date()); // Default to today's date
  const [endDate, setEndDate] = useState(null);
  const [selectedPG, setSelectedPG] = useState(preSelectedPG);
  const [selectedBed, setSelectedBed] = useState(preSelectedBed);
  const [payableAmount, setPayableAmount] = useState(parseFloat(preSelectedBed?.bed_rent) || 0);
  const [showSelectPG, setShowSelectPG] = useState(location.state?.showSelectPG || false); // State to toggle SelectPG component
  const [showSelectBed, setShowSelectBed] = useState(false); // State to toggle SelectBed component
  const [errors, setErrors] = useState({}); // State to track validation errors
  const [isAllocating, setIsAllocating] = useState(false); // State to track allocation saving
  
  // State for showing start allocation modal
  const [showStartModal, setShowStartModal] = useState(false);
  const [createdAllocation, setCreatedAllocation] = useState(null);
  
  // Discount states
  const [hasDiscount, setHasDiscount] = useState(false);
  const [discountType, setDiscountType] = useState('amount'); // 'amount' or 'percentage'
  const [discountValue, setDiscountValue] = useState('');
  const [discountedRent, setDiscountedRent] = useState(parseFloat(preSelectedBed?.bed_rent) || 0);
  
  // Advance amount state
  const [advanceAmount, setAdvanceAmount] = useState(parseFloat(preSelectedBed?.bed_rent) || 0);
  
  // Payment recording states
  const [recordPayment, setRecordPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentStartDate, setPaymentStartDate] = useState(new Date());
  const [paymentEndDate, setPaymentEndDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of current month
  });
  
  // Initialize payable amount and advance amount if bed is pre-selected
  useEffect(() => {
    if (preSelectedBed) {
      const bedRent = parseFloat(preSelectedBed.bed_rent) || 0;
      setPayableAmount(bedRent);
      setDiscountedRent(bedRent);
      setAdvanceAmount(bedRent);
    }
  }, [preSelectedBed]);

  // Calculate discounted rent whenever discount values change
  useEffect(() => {
    if (selectedBed && hasDiscount && discountValue) {
      const bedRent = parseFloat(selectedBed.bed_rent) || 0;
      let discount = 0;
      
      if (discountType === 'amount') {
        discount = parseFloat(discountValue) || 0;
        // Ensure discount doesn't exceed bed rent
        discount = Math.min(discount, bedRent);
      } else if (discountType === 'percentage') {
        const percentage = parseFloat(discountValue) || 0;
        // Ensure percentage is between 0 and 100
        const validPercentage = Math.min(Math.max(percentage, 0), 100);
        discount = (bedRent * validPercentage) / 100;
      }
      
      const finalRent = bedRent - discount;
      setDiscountedRent(finalRent);
      setPayableAmount(finalRent);
    } else if (selectedBed) {
      const bedRent = parseFloat(selectedBed.bed_rent) || 0;
      setDiscountedRent(bedRent);
      setPayableAmount(bedRent);
    }
  }, [selectedBed, hasDiscount, discountType, discountValue]);

  const validateInputs = () => {
    const newErrors = {};
    if (!startDate) {
      newErrors.startDate = 'Start Date is required.';
    }
    if (!selectedPG) {
      newErrors.selectedPG = 'Paying Guest must be selected.';
    }
    if (!selectedBed) {
      newErrors.selectedBed = 'Bed must be selected.';
    }
    if (recordPayment) {
      const amount = parseFloat(paymentAmount);
      if (!paymentAmount || isNaN(amount) || amount <= 0) {
        newErrors.paymentAmount = 'Payment amount is required and must be greater than 0.';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSelectPG = (pg) => {
    setSelectedPG(pg);
    setShowSelectPG(false); // Close SelectPG component
  };

  const handleSelectBed = (bed) => {
    setSelectedBed(bed);
    const bedRent = parseFloat(bed.bed_rent) || 0;
    setPayableAmount(bedRent); // Set initial payable amount
    setAdvanceAmount(bedRent); // Set initial advance amount to bed rent
    setShowSelectBed(false); // Close SelectBed component
    // Reset discount when new bed is selected
    setHasDiscount(false);
    setDiscountValue('');
  };

  const handleAllocate = async () => {
    if (validateInputs()) {
      // Build confirmation message with allocation details
      const lines = [
        `PG: ${selectedPG.name}`,
        `Bed: ${selectedBed.building_name} - ${selectedBed.room_name}`,
        `Start Date: ${formatDateToDDMMYYYY(startDate)}`,
      ];
      if (endDate) {
        lines.push(`End Date: ${formatDateToDDMMYYYY(endDate)}`);
      }
      lines.push(`Monthly Rent: ₹${payableAmount.toFixed(2)}${hasDiscount ? ' (Discount applied)' : ''}`);
      if (advanceAmount > 0) {
        lines.push(`Advance: ₹${advanceAmount.toFixed(2)}`);
      }
      if (recordPayment) {
        lines.push(`Payment: ₹${parseFloat(paymentAmount).toFixed(2)} (${formatDateToDDMMYYYY(paymentStartDate)} - ${formatDateToDDMMYYYY(paymentEndDate)})`);
      }

      const message = (
        <div style={{ textAlign: 'left' }}>
          {lines.map((line, i) => <div key={i}>{line}</div>)}
          <div style={{ marginTop: 8, fontWeight: 600 }}>Proceed with allocation?</div>
        </div>
      );

      const confirmed = await toastConfirm(true, message);
      if (confirmed) {
        await performAllocation(false);
      }
    }
  };

  const performAllocation = async (startAllocation) => {
    setIsAllocating(true);
    try {
      const allocationData = {
        start_date: formatDateToYMD(startDate),
        end_date: endDate ? formatDateToYMD(endDate) : null,
        pg_id: selectedPG.pg_id,
        bed_id: selectedBed.bed_id,
        room_id: selectedBed.room_id,
        discount_type: hasDiscount ? discountType : null,
        discount_value: hasDiscount ? parseFloat(discountValue) || 0 : null,
        total_amount: parseFloat(selectedBed.bed_rent) || 0,
        discounted_rent: hasDiscount ? discountedRent : null,
        payable_amount: payableAmount,
        advance_amount: advanceAmount,
        start_allocation: startAllocation, // Add the flag based on user choice
      };

      // Add payment data if record payment is checked
      if (recordPayment) {
        allocationData.record_payment = true;
        allocationData.payment_amount = parseFloat(paymentAmount);
        allocationData.payment_start_date = formatDateToYMD(paymentStartDate);
        allocationData.payment_end_date = formatDateToYMD(paymentEndDate);
      }

      const response = await apiPost('/allocation', allocationData);
      
      if (response.status === 200 || response.status === 201) {
        const allocationId = response.data?.allocation_id;
        
        // Check if start date is today or in the past
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const allocStartDate = new Date(startDate);
        allocStartDate.setHours(0, 0, 0, 0);
        const isStartDateTodayOrPast = allocStartDate <= today;
        
        if (isStartDateTodayOrPast && allocationId) {
          // Show start modal for immediate start
          const allocationForModal = {
            allocation_id: allocationId,
            pg_name: selectedPG.name,
            building_name: selectedBed.building_name,
            room_type: selectedBed.room_name,
            start_date: startDate,
            end_date: endDate,
            advance_amount: advanceAmount
          };
          setCreatedAllocation(allocationForModal);
          setShowStartModal(true);
        } else {
          toastSuccess('Bed allocated successfully! Will start on ' + formatDateToDDMMYYYY(startDate));
          if (allocationId) {
            navigate(`/bedallocation/allocation/${allocationId}`);
          }
        }
      }
    } catch (error) {
      console.error('Allocation failed:', error);
      if (error.response?.data?.detail) {
        toastError('Error allocating bed ' + error.response.data.detail);
      } else {
        toastError('Failed to allocate bed. Please try again.');
      }
    } finally {
      setIsAllocating(false);
    }
  };

  const handleStartModalSuccess = () => {
    setShowStartModal(false);
    toastSuccess('Bed allocated and started successfully! User account created.');
    if (createdAllocation?.allocation_id) {
      navigate(`/bedallocation/allocation/${createdAllocation.allocation_id}`);
    }
  };

  const handleStartModalCancel = () => {
    setShowStartModal(false);
    toastSuccess('Bed allocated successfully! Will start on ' + formatDateToDDMMYYYY(startDate));
    if (createdAllocation?.allocation_id) {
      navigate(`/bedallocation/allocation/${createdAllocation.allocation_id}`);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleClearPG = () => {
    setSelectedPG(null);
    setErrors({ ...errors, selectedPG: undefined });
  };

  const handleEditPG = () => {
    setSelectedPG(null);
    setShowSelectPG(true);
  };

  // Function to calculate end date (30 days after start date)
  const calculateEndDate = (date) => {
    if (!date) return null;
    const newEndDate = new Date(date);
    newEndDate.setDate(newEndDate.getDate() + 30);
    return newEndDate;
  };

  // Function to calculate minimum end date (30 days after start date)
  const getMinEndDate = () => {
    if (!startDate) return new Date();
    return calculateEndDate(startDate);
  };

  // Function to calculate maximum start date (1 month from today)
  const getMaxStartDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 1);
    return maxDate;
  };

  // Update the start date handler - remove automatic end date setting
  const handleStartDateChange = (date) => {
    setStartDate(date);
    // Update payment dates based on new start date
    if (date) {
      setPaymentStartDate(new Date(date));
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      setPaymentEndDate(monthEnd);
    }
    // Don't automatically set end date anymore
    if (errors.startDate) {
      setErrors({ ...errors, startDate: undefined });
    }
  };

  return (
    <div>
      {showSelectPG ? (
        <SelectPG onSelect={handleSelectPG} />
      ) : showSelectBed ? (
        <SelectBed onSelect={handleSelectBed} />
      ) : (
        <div className="rt-layout">
          <Menu className="rt-menu" activePath={location.pathname} />
          <div className="rt-main-content">
            <div className="rt-title-row">
              <h2 className="rt-title">Allocate Bed</h2>
              <div className="rt-actions">
                <RTButton variant="secondary" onClick={handleCancel}>
                  &#8592; Back
                </RTButton>
              </div>
            </div>
            <div className="allocate-bed-section">
              {/* Left Section */}
              <div className="allocate-bed-form">
                <h2 className="rt-form-title">Allocation Details</h2>
                
                <div className="rt-form-group">
                  <label>
                    Start Date <span style={{ color: 'red' }}>*</span>
                  </label>
                  <DatePicker
                    selected={startDate}
                    onChange={handleStartDateChange}  // Use the new handler
                    dateFormat="dd/MM/yyyy"
                    className="custom-datepicker rt-input"
                    maxDate={getMaxStartDate()} // Start date can be up to 1 month in the future
                    placeholderText="DD/MM/YYYY"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    autoComplete="off"
                  />
                  {errors.startDate && (
                    <div className="rt-form-error">{errors.startDate}</div>
                  )}
                </div>
                
                <div className="rt-form-group">
                  <label>End Date</label>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    dateFormat="dd/MM/yyyy"
                    className="custom-datepicker rt-input"
                    minDate={getMinEndDate()} // End date must be at least 30 days after start date
                    placeholderText="DD/MM/YYYY"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    autoComplete="off"
                  />
                  {errors.endDate && <div className="rt-form-error">{errors.endDate}</div>}
                </div>

                {/* Paying Guest Section - Now styled like other form groups */}
                <div className="rt-form-group">
                  <label>
                    Paying Guest <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div className="pg-selection-container">
                    {!selectedPG ? (
                      <div 
                        className="pg-selection-empty clickable"
                        onClick={() => setShowSelectPG(true)}
                      >
                        <span style={{ fontWeight: 'bold'}}>Select Paying Guest</span>
                      </div>
                    ) : (
                      <div className="pg-selection-filled">
                        <span className="selected-pg-name">{selectedPG.name}</span>
                        <div className="pg-actions">
                          <button 
                            className="icon-button edit-button" 
                            onClick={handleEditPG}
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button 
                            className="icon-button clear-button" 
                            onClick={handleClearPG}
                            title="Clear"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.selectedPG && (
                    <div className="rt-form-error">{errors.selectedPG}</div>
                  )}
                </div>
                
                <div className="rt-form-group">
                  <label>
                    Room and Bed <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div className="bed-selection-container">
                    {!selectedBed ? (
                      <div 
                        className="bed-selection-empty clickable"
                        onClick={() => setShowSelectBed(true)}
                      >
                        <span style={{ fontWeight: 'bold'}}>Select Room and Bed</span>
                      </div>
                    ) : (
                      <div className="bed-selection-filled">
                        <span className="selected-bed-name">
                          {selectedBed.building_name} : {selectedBed.room_name}
                        </span>
                        <div className="bed-actions">
                          <button 
                            className="icon-button edit-button" 
                            onClick={() => {
                              setSelectedBed(null);
                              setPayableAmount(0);
                              setShowSelectBed(true);
                            }}
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button 
                            className="icon-button clear-button" 
                            onClick={() => {
                              setSelectedBed(null);
                              setPayableAmount(0);
                              setErrors({ ...errors, selectedBed: undefined });
                            }}
                            title="Clear"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.selectedBed && (
                    <div className="rt-form-error">{errors.selectedBed}</div>
                  )}
                </div>
              </div>

              {/* Right Section - Summary */}
              <div className="allocate-bed-summary">
                <h2 className="summary-title">Allocation Summary</h2>
                
                {/* Rent Information */}
                <div className="summary-section">
                  <h3 className="summary-subtitle">Rent Information</h3>
                  <div className="allocate-summary-item">
                    <span className="allocate-summary-label">Bed Rent:</span>
                    <span className="allocate-summary-value">
                      {selectedBed ? `₹${selectedBed.bed_rent}` : '-'}
                    </span>
                  </div>
                  
                  {/* Advance Amount Input */}
                  {selectedBed && (
                    <div className="allocate-summary-item">
                      <span className="allocate-summary-label">Advance Amount (₹):</span>
                      <input
                        type="number"
                        className="allocate-inline-input"
                        value={advanceAmount}
                        onChange={(e) => setAdvanceAmount(parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  )}
                  
                  {/* Discount Section */}
                  {selectedBed && (
                    <>
                      <div className="allocate-summary-item allocate-discount-checkbox">
                        <label className="allocate-checkbox-label">
                          <input
                            type="checkbox"
                            checked={hasDiscount}
                            onChange={(e) => {
                              setHasDiscount(e.target.checked);
                              if (!e.target.checked) {
                                setDiscountValue('');
                                setDiscountType('amount');
                              }
                            }}
                          />
                          <span className="allocate-summary-label">Apply Discount</span>
                        </label>
                      </div>
                      
                      {hasDiscount && (
                        <>
                          <div className="allocate-discount-type">
                            <label className="allocate-radio-label">
                              <input
                                type="radio"
                                value="amount"
                                checked={discountType === 'amount'}
                                onChange={(e) => {
                                  setDiscountType(e.target.value);
                                  setDiscountValue('');
                                }}
                              />
                              Amount
                            </label>
                            <label className="allocate-radio-label">
                              <input
                                type="radio"
                                value="percentage"
                                checked={discountType === 'percentage'}
                                onChange={(e) => {
                                  setDiscountType(e.target.value);
                                  setDiscountValue('');
                                }}
                              />
                              Percentage
                            </label>
                          </div>
                          
                          <div className="allocate-summary-item">
                            <span className="allocate-summary-label">
                              Discount {discountType === 'percentage' ? '(%)' : '(₹)'}:
                            </span>
                            <input
                              type="number"
                              className="allocate-inline-input"
                              value={discountValue}
                              onChange={(e) => setDiscountValue(e.target.value)}
                              placeholder={discountType === 'percentage' ? '0-100' : '0'}
                              min="0"
                              max={discountType === 'percentage' ? '100' : selectedBed.bed_rent}
                            />
                          </div>
                          
                          <div className="allocate-summary-item">
                            <span className="allocate-summary-label">Discounted Rent:</span>
                            <span className="allocate-summary-value">
                              ₹{discountedRent.toFixed(2)}
                            </span>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* Payment Section */}
                {selectedBed && (
                  <div className="summary-section">
                    <div className="allocate-summary-item">
                      <label className="allocate-checkbox-label">
                        <input
                          type="checkbox"
                          checked={recordPayment}
                          onChange={(e) => {
                            setRecordPayment(e.target.checked);
                            if (!e.target.checked) {
                              setPaymentAmount('');
                            }
                          }}
                        />
                        <span className="allocate-summary-label" style={{ fontWeight: 'bold' }}>Record Payment</span>
                      </label>
                    </div>
                    
                    {recordPayment && (
                      <>
                        <div className="allocate-summary-item">
                          <span className="allocate-summary-label">Start Date:</span>
                          <span className="allocate-summary-value">
                            {formatDateToDDMMYYYY(paymentStartDate)}
                          </span>
                        </div>
                        
                        <div className="allocate-summary-item">
                          <span className="allocate-summary-label">End Date:</span>
                          <DatePicker
                            selected={paymentEndDate}
                            onChange={(date) => setPaymentEndDate(date)}
                            dateFormat="dd/MM/yyyy"
                            className="custom-datepicker allocate-inline-input"
                            minDate={paymentStartDate}
                            placeholderText="DD/MM/YYYY"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            autoComplete="off"
                            popperPlacement="bottom-end"
                          />
                        </div>
                        
                        <div className="allocate-summary-item">
                          <span className="allocate-summary-label">Payment Amount (₹):</span>
                          <div className="allocate-field-wrapper">
                            <input
                              type="number"
                              className="allocate-inline-input"
                              value={paymentAmount}
                            onChange={(e) => {
                              setPaymentAmount(e.target.value);
                              if (errors.paymentAmount) {
                                setErrors((prev) => ({ ...prev, paymentAmount: undefined }));
                              }
                            }}
                              placeholder="0"
                              min="1"
                            />
                            {errors.paymentAmount && (
                              <div className="rt-form-error">{errors.paymentAmount}</div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Total Section */}
                <div className="allocate-summary-total">
                  <div className="allocate-summary-item">
                    <span className="allocate-summary-label">Advance Amount:</span>
                    <span className="allocate-summary-value total-amount">
                      {advanceAmount && advanceAmount > 0 ? `₹${advanceAmount.toFixed(2)}` : '-'}
                    </span>
                  </div>
                  <div className="allocate-summary-item">
                    <span className="allocate-summary-label">Monthly Payable Rent:</span>
                    <span className="allocate-summary-value total-amount">
                      {payableAmount && payableAmount > 0 ? `₹${payableAmount.toFixed(2)}` : '-'}
                    </span>
                  </div>
                </div>

                {/* Allocate Button - Moved to Summary */}
                <div className="summary-actions">
                  <RTButton
                    variant="primary"
                    disabled={!startDate || !selectedPG || !selectedBed || isAllocating}
                    onClick={handleAllocate}
                  >
                    {isAllocating ? (
                      <>
                        <RTSpinner fullScreen={true} />
                      </>
                    ) : (
                      'Allocate'
                    )}
                  </RTButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Update Allocation Status Modal */}
      <UpdateAllocationStatusModal
        isOpen={showStartModal}
        mode="start"
        allocation={createdAllocation}
        onSuccess={handleStartModalSuccess}
        onCancel={handleStartModalCancel}
      />
    </div>
  );
};

export default AllocateBed;